import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import haveAccountImage from 'images/illustrations/have-account.webp';
import resumeWizardIllustration from 'images/illustrations/resume-wizard.webp';
import { List } from 'immutable';
import debounce from 'just-debounce';
import {
  Onboarding,
  OnboardingContext,
  OnboardingSubtitle,
  OnboardingTitle,
} from 'registration/containers/Onboarding';
import { useInterval } from 'usehooks-ts';

import { Drawer, PrimaryButton, SIZE } from 'shared/components/ihcl/drawer';
import { LucideIcon } from 'shared/components/ihcl/lucideIcon';
import { Modal } from 'shared/components/ihcl/modal';
import {
  Center,
  VerticalSpacedItems,
} from 'shared/components/ihcl/positioning';
import { styled } from 'shared/components/ihcl/styled';
import { Text } from 'shared/components/ihcl/text';
import { Button } from 'shared/components/ihcl/v2/button';
import { Spinner } from 'shared/components/ihcl/v2/spinner';
import {
  parseServerWorkExperience,
  WorkExperience,
} from 'shared/components/WorkExperienceControl';
import {
  getUserVariant,
  onboardingResumeParseUploadCopyVariantsMap,
} from 'shared/helpers/activateABTest';
import Sentry from 'shared/helpers/sentry';
import {
  trackClick,
  trackEvent,
  trackExperimentAssignment,
} from 'shared/helpers/tracking';

import { useResumeParse } from 'api/talent/hooks/resumeParse';

import { DrawerContents, HeaderImage } from './OnboardingCareSettings';

const ONBOARDING_RESUME_UPLOAD_START_SAMPLE_RATE =
  window.IH?.sentry?.feature_sample_rates?.resume_upload_start || 0;
const ONBOARDING_RESUME_UPLOAD_UPLOAD_SAMPLE_RATE =
  window.IH?.sentry?.feature_sample_rates?.resume_upload_uploaded || 0;

const UploadResumeWrapper = styled('div', ({ $theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: $theme.sizing.unit16,
  flexWrap: 'nowrap',
  marginBottom: $theme.sizing.unit16,
}));
const UploadResumeBody = styled('div', ({ $theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: $theme.sizing.unit16,
  paddingTop: $theme.sizing.unit40,
  paddingBottom: $theme.sizing.unit16,
  textAlign: 'center',
}));

const Centered = styled('div', () => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));
const valueProps = [
  'Processing resume...',
  'Identifying education...',
  'Analyzing experience...',
  'Building profile...',
  'Scanning jobs...',
];

const trackResumeUploadClick = debounce(() => {
  trackClick('Resume upload clicked');
}, 500);

const UploadResume = ({
  isUploadingResume = false,
  sendResumeToServer,
  onNotRightNow,
}: {
  isUploadingResume?: boolean;
  sendResumeToServer: (files: FileList) => void;
  onNotRightNow: () => void;
}) => {
  const [currentValueProp, setCurrentValueProp] = useState(0);
  const fileInputRef = useRef(null);
  useInterval(() => {
    if (isUploadingResume) {
      setCurrentValueProp((prev) => (prev + 1) % valueProps.length);
    }
  }, 2500);
  const inExperimentA = useMemo(() => {
    const variant = getUserVariant(
      'onboardingResumeParseUploadCopy',
      onboardingResumeParseUploadCopyVariantsMap
    );
    const trackingVariant = variant === 'unenrolled' ? null : variant;
    trackExperimentAssignment(
      'onboarding_resume_parse_upload_copy',
      trackingVariant
    );
    return variant === 'a';
  }, []);

  return (
    <>
      <UploadResumeWrapper
        onClick={() => {
          fileInputRef.current?.click();
          trackResumeUploadClick();
        }}
      >
        {inExperimentA ? (
          <UploadResumeBody>
            <HeaderImage src={resumeWizardIllustration} alt="Resume wizard" />
            <Text variant="HeadingMediumBold">
              Upload your resume to get job matches faster
            </Text>
            <Text variant="ParagraphMedium" color="bodyCopyGray">
              We&apos;ll use your resume to match you with jobs that align with
              your skills and experience.
            </Text>
          </UploadResumeBody>
        ) : (
          <div>
            <OnboardingTitle>Have a resume available?</OnboardingTitle>
            <Center>
              <OnboardingSubtitle>
                No worries, if not. We&apos;ll just ask some quick questions to
                get your job matches.
              </OnboardingSubtitle>
            </Center>
          </div>
        )}
        <Button
          color={inExperimentA ? 'alternate' : 'default'}
          variant={inExperimentA ? 'primary' : 'outline'}
        >
          {inExperimentA ? 'Yes, upload now' : 'Yes - Upload it now'}
          <LucideIcon name="Upload" />
        </Button>
        <input
          type="file"
          accept=".pdf,.doc,.docx"
          style={{ display: 'none' }}
          ref={fileInputRef}
          onChange={(event) => sendResumeToServer(event.target.files)}
        />
      </UploadResumeWrapper>
      <Centered>
        <Button
          color={inExperimentA ? 'neutral' : 'default'}
          onClick={onNotRightNow}
          variant={inExperimentA ? 'minimal' : 'outline'}
        >
          {inExperimentA ? "I'll do this later" : 'Not right now'}
        </Button>
      </Centered>
      <Modal
        closeable={false}
        isOpen={isUploadingResume}
        size="full"
        overrides={{
          Close: {
            style: () => ({
              display: 'none',
            }),
          },
          Dialog: {
            style: {
              marginTop: 0,
              marginRight: 0,
              marginBottom: 0,
              marginLeft: 0,
            },
          },
        }}
      >
        <VerticalSpacedItems
          style={{
            height: '100%',
            justifyContent: 'space-around',
          }}
        >
          <Center
            style={{
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            <OnboardingSubtitle>Processing your resume</OnboardingSubtitle>
            <Spinner className="ml-[calc(50%-16px)] size-8" />
            <OnboardingSubtitle>
              {valueProps[currentValueProp]}
            </OnboardingSubtitle>
          </Center>
          <Center>
            This process may take 15 seconds or more...
            <br />
            we promise it’s worth the wait!
          </Center>
        </VerticalSpacedItems>
      </Modal>
    </>
  );
};

const cleanCertifications = (certifications) => {
  if (!certifications || certifications.length === 0) return [];
  return certifications.map((cert) => cert.split(';')[0].trim());
};

const parseSpecialties = (specialties, onboardingOptions) => {
  const nurseSpecialties = new Map();
  if (!specialties || specialties.length === 0) return nurseSpecialties;
  specialties.forEach((specialty) => {
    if (specialty.confidence === 'high') {
      const specialtyId = onboardingOptions.nurse_specialty_descriptions.find(
        (nsd) => nsd.description === specialty.specialty
      ).id;
      nurseSpecialties.set(specialtyId, specialty.years_of_experience);
    }
  });
  return nurseSpecialties;
};

const parseStateLicenses = (stateLicenses, onboardingOptions) => {
  const states = onboardingOptions.states.filter((state) =>
    stateLicenses.includes(state.abbreviation)
  );
  return states;
};

const getMostRecentEducation = (educationExperiences) => {
  if (!educationExperiences || educationExperiences.length === 0) return null;
  educationExperiences.sort((a, b) => {
    const yearA = Number(a.graduation_year);
    const yearB = Number(b.graduation_year);
    return yearB - yearA; // Sort descending by year
  });
  return educationExperiences[0].degree_type.split(';')[0].trim();
};

type OnboardingResumeUploadProps = {
  nextAction?: string;
  onboardingWrapper?: React.ComponentType<any> | null;
  onboardingWrapperProps?: Record<string, any>;
  previousAction?: string;
  stepNumber: number;
};

export const OnboardingResumeUpload = ({
  nextAction = null,
  onboardingWrapper = null,
  onboardingWrapperProps = {},
  previousAction = null,
  stepNumber,
}: OnboardingResumeUploadProps) => {
  const {
    uploadResume,
    isComplete,
    isError,
    isTimeout,
    isAccountInfoComplete,
    accountInfo,
    resumeBasicResults,
    resumeWorkExperienceResults,
    resumeSpecialtyResults,
    status,
    taskId,
  } = useResumeParse();
  const [isUploadingResume, setIsUploadingResume] = useState(false);
  const [resumeProcessed, setResumeProcessed] = useState(false);
  const [seenResumeUploadDrawer, setSeenResumeUploadDrawer] = useState(false);
  const [skippedUpload, setSkippedUpload] = useState(false);
  const [stepKeys, setStepKeys] = useState([]);
  const { onboardingData, onboardingOptions, setOnboardingData }: any =
    useContext(OnboardingContext);
  const stepName = 'Automatic Resume Processing Upload';
  useEffect(() => {
    if (Math.random() < ONBOARDING_RESUME_UPLOAD_START_SAMPLE_RATE) {
      Sentry.getReplay()?.flush();
    }
  }, []);

  useEffect(() => {
    if (isComplete && !isError && !onboardingData.containsParsedResume) {
      const parsedResumeData = {
        account_info: accountInfo,
        ...resumeBasicResults,
        acute_care_experience:
          resumeBasicResults.worked_in_hospital_in_the_last_year ? 'yes' : null,
        clinical_care_experience:
          resumeBasicResults.years_experience > 0 ? 'yes' : null,
        education_level: getMostRecentEducation(
          resumeBasicResults.talent_education_experiences
        ),
        email: resumeBasicResults.email || '',
        certifications: cleanCertifications(resumeBasicResults.certifications),
        state_licenses: parseStateLicenses(
          resumeBasicResults.state_licenses,
          onboardingOptions
        ),
        nurseSpecialties: parseSpecialties(
          resumeSpecialtyResults.specialties,
          onboardingOptions
        ),
        ...resumeWorkExperienceResults,
        workExperiences: List<WorkExperience>().withMutations(
          (workExperienceList) => {
            resumeWorkExperienceResults.work_experiences.forEach((we) => {
              workExperienceList.push(parseServerWorkExperience(we));
            });
          }
        ),
        containsParsedResume: true,
        resume_parse_task_id: taskId,
      };
      setStepKeys(Object.keys(parsedResumeData));
      setOnboardingData({
        ...onboardingData,
        ...parsedResumeData,
      });
      if (!resumeProcessed) {
        setResumeProcessed(true);
      }
    }

    // TODO: Rework useResumeParse to expose a callbacks for complete, timeout,
    // and error. Onboarding already has complex state management which is
    // driven by side-effects, and calling setOnboardingData via another
    // useEffect adds to that complexity.
    if (isComplete && isTimeout && !skippedUpload) {
      setIsUploadingResume(false);
      setStepKeys(['skip', 'resume_parse_task_id']);
      setOnboardingData({
        ...onboardingData,
        resume_parse_task_id: taskId,
      });
      setSkippedUpload(true);
      Sentry.addBreadcrumb({
        category: 'onboarding_resume_upload',
        message: 'Resume upload timed out',
        level: 'error',
        data: {
          resume_parse_task_id: taskId,
        },
      });
    }
  }, [
    accountInfo,
    isComplete,
    isError,
    isTimeout,
    onboardingData,
    onboardingOptions,
    resumeBasicResults,
    resumeProcessed,
    resumeSpecialtyResults,
    resumeWorkExperienceResults,
    setOnboardingData,
    skippedUpload,
    status,
    taskId,
  ]);

  const sendResumeToServer = (incomingFiles: FileList) => {
    if (incomingFiles.length > 0 && !resumeProcessed) {
      uploadResume(incomingFiles[0]);
      setIsUploadingResume(true);

      if (incomingFiles.length > 1) {
        Sentry.captureMessage(
          `Multiple files uploaded ${incomingFiles.length} for resume, only the first file will be used.`
        );
      }

      Sentry.setTag('feature.onboarding_resume_upload', true);
      Sentry.addBreadcrumb({
        category: 'onboarding_resume_upload',
        message: 'Resume upload started',
        level: 'info',
      });
      if (Math.random() < ONBOARDING_RESUME_UPLOAD_UPLOAD_SAMPLE_RATE) {
        Sentry.getReplay()?.flush();
      }
    }
    trackEvent('Resume upload file selected');
  };

  const onNotRightNow = () => {
    setStepKeys(['skip']);
    setSkippedUpload(true);
    trackClick('Resume upload skipped');

    Sentry.setTag('feature.onboarding_resume_upload', false);
    Sentry.addBreadcrumb({
      category: 'onboarding_resume_upload',
      message: 'Resume upload skipped',
      level: 'info',
    });
  };

  const shouldShowSignInDrawer =
    !seenResumeUploadDrawer &&
    isAccountInfoComplete &&
    accountInfo?.email_in_use;
  return (
    <Onboarding
      autoProgress={!shouldShowSignInDrawer}
      currentStep={stepNumber}
      isNextDisabled={!resumeProcessed && !skippedUpload}
      nextAction={nextAction}
      previousAction={previousAction}
      stepKeys={stepKeys}
      stepName={stepName}
      onboardingWrapper={onboardingWrapper}
      onboardingWrapperProps={onboardingWrapperProps}
    >
      <UploadResume
        isUploadingResume={isUploadingResume}
        sendResumeToServer={sendResumeToServer}
        onNotRightNow={onNotRightNow}
      />
      <Drawer
        isOpen={shouldShowSignInDrawer}
        size={SIZE.default}
        onClose={() => setSeenResumeUploadDrawer(true)}
        actions={[
          <PrimaryButton
            key="sign-in-button"
            onClick={() => {
              window.location.href = '/users/sign_in';
            }}
          >
            Sign in
          </PrimaryButton>,
        ]}
      >
        <DrawerContents>
          <HeaderImage src={haveAccountImage} alt="Bird in a birdhouse" />
          <OnboardingSubtitle>
            <strong>Looks like you already have an account with us.</strong>
          </OnboardingSubtitle>
          <OnboardingSubtitle $noTopMargin>
            Please sign in to continue your journey, no need to start over.
          </OnboardingSubtitle>
        </DrawerContents>
      </Drawer>
      <Drawer
        isOpen={isError && !isTimeout && !skippedUpload}
        closeable={false}
        actions={[
          <PrimaryButton
            key="continue-button"
            onClick={() => {
              setIsUploadingResume(false);
              setStepKeys(['skip', 'resume_parse_task_id']);
              setOnboardingData({
                ...onboardingData,
                resume_parse_task_id: taskId,
              });
              setSkippedUpload(true);
              Sentry.addBreadcrumb({
                category: 'onboarding_resume_upload',
                message: 'Resume upload error',
                level: 'error',
              });
            }}
          >
            Continue
          </PrimaryButton>,
        ]}
      >
        <DrawerContents>
          <OnboardingSubtitle>
            <strong>There was an error processing your resume.</strong>
          </OnboardingSubtitle>
          <OnboardingSubtitle $noTopMargin>
            We couldn’t process your resume right now, but no worries. You can
            still complete your profile, and we’ll get your job matches.
          </OnboardingSubtitle>
        </DrawerContents>
      </Drawer>
    </Onboarding>
  );
};

export default OnboardingResumeUpload;
